import api from "config/axiosConfig";
import type { CoauthorSuggestion, CoauthorType } from "types/coauthor";
import { apiRoutes } from "util/apiRoutes";
import { isPostBackedKind } from "util/entityMetadata";

export interface EntityDetailOptions {
  includePlacementDetails?: boolean;
}

/**
 * Normalized entity payload from various API detail endpoints.
 */
export type EntityData = {
  id: number;
  title?: string;
  name?: string;
  username?: string;
  teamname?: string;
  display_name?: string;
  entity_name?: string;
  cname?: string;
  kind?: string;
  type?: string;
  identity_mode?: string;
  user_id?: number | string | null;
  issue_id?: number | string | null;
  subject_cname?: string;
  topic_cname?: string;
  issue_properties?: Record<string, unknown>;
  related_issue?: Record<string, unknown>;
  related_issues?: Array<Record<string, unknown>>;
  properties?: Record<string, unknown>;
  properties_public?: Record<string, unknown>;
  placement?: IssuePlacementData | null;
};

export type IssuePlacementData = EntityData & {
  placement_kind: "issue";
};

const ENTITY_NUMERIC_ID_FIELDS = [
  "id",
  "user_id",
  "issue_id",
  "parent_id",
  "root_id",
  "embed_post_id",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parsePositiveInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

function normalizeNumericIdValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  const parsed = parsePositiveInt(value);
  return parsed ?? value;
}

function normalizeEntityNumericIds(entity: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...entity };
  for (const key of ENTITY_NUMERIC_ID_FIELDS) {
    if (key in normalized) {
      normalized[key] = normalizeNumericIdValue(normalized[key]);
    }
  }
  return normalized;
}

function normalizeIssueEntity(entity: Partial<EntityData> & Record<string, unknown>): EntityData {
  const rawCname = typeof entity.cname === "string" && entity.cname.length > 0 ? entity.cname : "";
  const displayName =
    typeof entity.name === "string" && entity.name.trim().length > 0
      ? entity.name.trim()
      : rawCname.replace(/_/g, " ");
  const cname = rawCname.length > 0 ? rawCname : displayName.replace(/ /g, "_");
  return {
    ...entity,
    id: parsePositiveInt(entity.id) ?? 0,
    name: displayName,
    cname,
    entity_name: displayName,
  };
}

function normalizeIssuePlacement(placement: unknown): IssuePlacementData | null {
  if (!isRecord(placement)) {
    return null;
  }
  const normalized = normalizeIssueEntity({
    ...normalizeEntityNumericIds(placement as Record<string, unknown>),
    type: "issue",
  });
  return {
    ...normalized,
    placement_kind: "issue",
  };
}

async function attachPostActorDetails(entityType: string, entity: EntityData): Promise<EntityData> {
  if (!isPostBackedKind(entityType)) {
    return entity;
  }
  const actorId = parsePositiveInt(entity.user_id);
  const privacy =
    isRecord(entity.properties) && typeof entity.properties.privacy === "string"
      ? entity.properties.privacy
      : undefined;
  const needsActorFetch =
    actorId !== null &&
    entity.identity_mode !== "anonymous" &&
    privacy !== "anonymous" &&
    (typeof entity.username !== "string" || entity.username.trim().length === 0);

  if (!needsActorFetch) {
    return entity;
  }

  try {
    const actor = await getActorDetails("user", actorId);
    return {
      ...entity,
      username: actor.username ?? entity.username,
      display_name: actor.display_name ?? entity.display_name,
      properties_public: isRecord(entity.properties_public)
        ? entity.properties_public
        : actor.properties_public,
    };
  } catch {
    return entity;
  }
}

function applyPlacementCompatibilityAliases(
  entityType: string,
  entity: EntityData
): EntityData {
  const placement = normalizeIssuePlacement(entity.placement);
  if (!placement) {
    return entity;
  }

  const next: EntityData = {
    ...entity,
    placement,
  };

  if (typeof next.cname !== "string" || next.cname.trim().length === 0) {
    next.cname = placement.cname;
  }
  if (!isRecord(next.issue_properties) && isRecord(placement.properties)) {
    next.issue_properties = placement.properties;
  }

  if (entityType === "impact" || entityType === "teabate") {
    if (typeof next.subject_cname !== "string" || next.subject_cname.trim().length === 0) {
      next.subject_cname = placement.subject_cname ?? placement.cname;
    }
    if (typeof next.topic_cname !== "string" || next.topic_cname.trim().length === 0) {
      next.topic_cname = placement.topic_cname;
    }
  }

  if (entityType === "action" && !isRecord(next.related_issue)) {
    next.related_issue = placement;
  }

  if (
    entityType === "solution" &&
    (!Array.isArray(next.related_issues) || next.related_issues.length === 0)
  ) {
    next.related_issues = [placement];
  }

  return next;
}

/**
 * Resolve the GET URL for entity detail fetches (no legacy `/api/{kind}s/id/...` paths).
 */
const resolveEntityDetailUrl = (entityType: string, entityId: string | number): string => {
  if (isPostBackedKind(entityType)) {
    return apiRoutes.posts.byId(entityId);
  }
  if (entityType === "issue") {
    return apiRoutes.issues.byId(entityId);
  }
  if (entityType === "comment") {
    return apiRoutes.comments.byId(entityId);
  }
  throw new Error(`ENTITY_DETAIL_ROUTE_UNSUPPORTED:${entityType}`);
};

export const getEntityDetails = async (
  entityType: string,
  entityId: string | number,
  options: EntityDetailOptions = {}
): Promise<EntityData> => {
  try {
    const url = resolveEntityDetailUrl(entityType, entityId);
    const response = await api.get<EntityData>(
      url,
      isPostBackedKind(entityType) && options.includePlacementDetails
        ? { params: { includePlacementDetails: true } }
        : undefined
    );
    const entity = normalizeEntityNumericIds({
      ...(response.data ?? { id: Number(entityId) }),
      type: entityType,
    }) as EntityData;

    /**
     * Impact/teabate posts: `title` is the post headline (often `[no_title]`). The issue headline
     * belongs in `placement`, not in top-level `name`.
     */
    if (entityType === "impact" || entityType === "teabate") {
      entity.entity_name = entity.title;
    } else if (entityType === "feedback") {
      entity.entity_name = entity.title;
      /** Posts expose `title`; legacy graph/list code still expects `name` for display. */
      if (typeof entity.title === "string" && entity.title.trim().length > 0) {
        if (typeof entity.name !== "string" || entity.name.trim().length === 0) {
          entity.name = entity.title;
        }
      }
    } else if (entityType === "solution") {
      entity.entity_name = entity.title;
      if (typeof entity.title === "string" && entity.title.trim().length > 0) {
        if (typeof entity.name !== "string" || entity.name.trim().length === 0) {
          entity.name = entity.title;
        }
      }
    } else if (entityType === "action") {
      entity.entity_name = entity.title;
      if (typeof entity.title === "string" && entity.title.trim().length > 0) {
        if (typeof entity.name !== "string" || entity.name.trim().length === 0) {
          entity.name = entity.title;
        }
      }
    } else if (entityType === "issue") {
      return normalizeIssueEntity(entity);
    }

    let normalized = applyPlacementCompatibilityAliases(entityType, entity);

    if (
      options.includePlacementDetails &&
      isPostBackedKind(entityType) &&
      normalized.placement === undefined
    ) {
      const placementIssueId = parsePositiveInt(normalized.issue_id);
      if (placementIssueId) {
        const placement = await getEntityDetails("issue", placementIssueId);
        normalized = applyPlacementCompatibilityAliases(entityType, {
          ...normalized,
          placement: {
            ...placement,
            placement_kind: "issue",
          },
        });
      } else {
        normalized = {
          ...normalized,
          placement: null,
        };
      }
    }

    return attachPostActorDetails(entityType, normalized);
  } catch (error) {
    console.error(`Error fetching ${entityType} details:`, error);
    return {
      id: Number(entityId),
      title: "Unknown entity",
      entity_name: "Unknown entity",
    };
  }
};

export const getActorDetails = async (actorType: string, actorId: string | number): Promise<EntityData> => {
  try {
    const actorRoute =
      actorType === "user" ? apiRoutes.user.byId(actorId) : apiRoutes.team.byId(actorId);
    const response = await api.get<EntityData>(actorRoute);
    const actor = response.data;
    if (actorType === "user") {
      actor.entity_name = actor.username;
    } else if (actorType === "team") {
      actor.entity_name = actor.teamname;
    }

    return actor;
  } catch (error) {
    console.error(`Error fetching ${actorType} details:`, error);
    return {
      id: Number(actorId),
      title: "Unknown actor",
      entity_name: "Unknown actor",
    };
  }
};

/**
 * Retrieve pending coauthor invites for a specific entity
 *
 * @param entityType - The type of entity (impact, solution, etc.)
 * @param entityId - The ID of the entity
 * @returns Array of pending coauthor objects with details
 */
export const getPendingCoauthorRequests = async (
  entityType: string,
  entityId: string | number
): Promise<CoauthorSuggestion[]> => {
  try {
    const response = await api.get(apiRoutes.requests.root, {
      params: {
        requestType: "coauthor_invite",
        entityType,
        entityId,
        status: "pending",
      },
    });

    const pendingCoauthors: CoauthorSuggestion[] = [];

    for (const request of response.data.requests as Array<{
      requestee_type: string;
      requestee_id: number;
      id: number;
    }>) {
      const requestee = await getActorDetails(request.requestee_type, request.requestee_id);
      const coauthorType: CoauthorType = request.requestee_type === "team" ? "team" : "user";
      const label =
        request.requestee_type === "user"
          ? requestee.username ?? requestee.entity_name ?? ""
          : requestee.teamname ?? requestee.entity_name ?? "";

      pendingCoauthors.push({
        id: requestee.id,
        type: coauthorType,
        name: label,
        display_name: requestee.display_name,
        request_id: request.id,
        isPending: true,
      });
    }

    return pendingCoauthors;
  } catch (error) {
    console.error(`Error fetching pending coauthor requests:`, error);
    return [];
  }
};

/**
 * Determine if current user can view followers of another user
 *
 * @param profileUser - The profile user object
 * @returns True if the current user can view the followers, false otherwise
 */
export const canViewUserFollow = async (profileUser: {
  properties_public?: { is_private?: boolean };
  viewer_relation?: string;
}): Promise<boolean> => {
  if (profileUser?.properties_public?.is_private) {
    return profileUser?.viewer_relation && profileUser?.viewer_relation?.match(/follower|bestea|admin|self/)
      ? true
      : false;
  }
  return true;
};

/**
 * Determine if current user can view followers and team members of a partea
 * @param profileTeam: any
 * @returns True if the current user can view the followers and team members, false otherwise
 */
export const canViewTeamFollow = async (profileTeam: {
  properties_public?: { is_private?: boolean };
  viewer_relation?: string;
}): Promise<boolean> => {
  if (profileTeam?.properties_public?.is_private) {
    return profileTeam?.viewer_relation &&
      profileTeam?.viewer_relation?.match(/admin|creator|moderator|member|follower|bestea/)
      ? true
      : false;
  }
  return true;
};
